/**
 * What narrows the Deactivated Profiles list, and how each dimension reads.
 *
 * A pure module, testable without a store or a render: the toolbar picks values
 * here, the gateway turns them into a query string, and neither knows what a
 * "last 30 days" is.
 */

/** How a profile left the scope: the values of `deactivation_reason`. */
export type ExclusionMethod = 'scan' | 'enrichment' | 'manual';

/**
 * The date presets, rather than two free date fields.
 *
 * An admin arbitrating false positives asks "what did the last scan cut" and
 * "what happened this month", not "what happened between the 3rd and the 17th".
 * Presets also keep the filter serialisable and re-evaluated at fetch time: a
 * page left open overnight still means "last 7 days" the next morning, where two
 * stored instants would quietly go stale.
 */
export type DateRange = 'all' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth';

export interface DeactivatedFilters {
  /** Free text over name, company and job title: the endpoint's `q`. */
  search: string;
  method: ExclusionMethod | null;
  /** A blocklist term that fired on the profile, as the blocklist stores it. */
  keyword: string | null;
  /**
   * A profile type the profile carries (#702): finds the profiles a rejection
   * keyword caught although they fit a type.
   */
  type: string | null;
  range: DateRange;
}

/** The shape the page opens with, and the one "Clear" returns to. */
export const NO_FILTERS: DeactivatedFilters = {
  search: '',
  method: null,
  keyword: null,
  type: null,
  range: 'all',
};

/**
 * The scope of the "Deactivated this month" figure (#612): the This month date
 * preset and nothing else, so the rows it shows are the ones the figure counts.
 */
export const THIS_MONTH_ONLY: DeactivatedFilters = { ...NO_FILTERS, range: 'thisMonth' };

/** Whether the list is under the This month preset alone: that figure's scope. */
export function isThisMonthOnly(filters: DeactivatedFilters): boolean {
  return (
    filters.range === 'thisMonth' &&
    filters.search.trim() === '' &&
    filters.method === null &&
    filters.keyword === null &&
    filters.type === null
  );
}

/** Whether anything is narrowing the list; drives "Clear" and the empty state. */
export function hasActiveFilters(filters: DeactivatedFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.method !== null ||
    filters.keyword !== null ||
    filters.type !== null ||
    filters.range !== 'all'
  );
}

export const METHOD_LABELS: Record<ExclusionMethod, string> = {
  scan: 'Scan',
  enrichment: 'Enrichment',
  manual: 'Manual',
};

export const RANGE_LABELS: Record<DateRange, string> = {
  all: 'Any time',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
};

/** Resolved bounds: `from` inclusive, `to` exclusive, `null` for "no bound". */
export interface DateBounds {
  from: Date | null;
  to: Date | null;
}

/**
 * A preset, as of `now`.
 *
 * The upper bound is EXCLUSIVE and lands on a midnight, so "last month" ends at
 * the first instant of this month rather than at its own last midnight, which
 * would silently drop everything deactivated during the final day.
 *
 * The rolling ranges have no upper bound at all: "last 7 days" means "since",
 * and capping it at now would race the clock against rows written a moment ago.
 */
export function resolveDateRange(range: DateRange, now: Date = new Date()): DateBounds {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (range) {
    case 'all':
      return { from: null, to: null };
    case 'last7':
      return { from: daysBefore(startOfToday, 6), to: null };
    case 'last30':
      return { from: daysBefore(startOfToday, 29), to: null };
    case 'thisMonth':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: null };
    case 'lastMonth':
      return {
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        to: new Date(now.getFullYear(), now.getMonth(), 1),
      };
  }
}

/**
 * `n` days back from a midnight, through the date parts rather than by
 * subtracting milliseconds: a DST boundary makes a day 23 or 25 hours long, and
 * the arithmetic version lands an hour off and shifts the whole range.
 */
function daysBefore(midnight: Date, days: number): Date {
  return new Date(midnight.getFullYear(), midnight.getMonth(), midnight.getDate() - days);
}
